// /accounts/$accountSlug/tenants/$tenantSlug/secrets — metadata-only
// table + "Create secret" Modal. No plaintext column anywhere in the
// DOM, ever. List rows link to the per-secret detail page (which is
// also metadata-only).

import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";

import { tenantSecrets } from "../../api/endpoints/tenantSecrets";
import { adminQueryOptions } from "../../api/adminQueryOptions";
import { qk } from "../../api/queryKeys";
import { EmptyState } from "../../components/ui/EmptyState";
import { ErrorBanner } from "../../components/ui/ErrorBanner";
import { Modal } from "../../components/ui/Modal";
import { Pill } from "../../components/ui/Pill";
import { Table } from "../../components/ui/Table";
import { PageHeader } from "../../components/shell/PageHeader";
import type { TenantSecretOut } from "../../api/types/tenantSecret";

import { TenantSecretCreateForm } from "./TenantSecretCreateForm";

interface TenantSecretListPageProps {
  accountSlug: string;
  tenantSlug: string;
}

export function TenantSecretListPage({
  accountSlug,
  tenantSlug,
}: TenantSecretListPageProps) {
  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);

  const q = useQuery({
    ...adminQueryOptions,
    queryKey: qk.tenantSecrets(accountSlug, tenantSlug),
    queryFn: () =>
      tenantSecrets.list(accountSlug, tenantSlug).then((r) => r.data),
  });

  // Mirror `creating` from the form (Modal close-disabled pattern from
  // AccountDetailPage). The form fires onPendingChange.
  return (
    <>
      <PageHeader
        title={`Secrets · ${tenantSlug}`}
        subtitle="Metadata only — plaintext is never echoed or displayed by this UI."
        actions={
          <button
            type="button"
            onClick={() => setCreateOpen(true)}
            style={primaryBtn}
          >
            Create secret
          </button>
        }
      />

      {q.isPending && <p style={{ color: "var(--text-dim)" }}>Loading…</p>}
      {q.error && <ErrorBanner error={q.error} title="Failed to list secrets" />}
      {q.data && (
        <Table<TenantSecretOut>
          columns={[
            {
              key: "ref",
              header: "Ref",
              render: (s) => (
                <Link
                  to="/accounts/$accountSlug/tenants/$tenantSlug/secrets/$secretId"
                  params={{ accountSlug, tenantSlug, secretId: s.id }}
                  style={{ color: "var(--accent)" }}
                >
                  {s.ref}
                </Link>
              ),
            },
            {
              key: "encryption_key_version",
              header: "KEK version",
              render: (s) => <Pill tone="neutral">v{s.encryption_key_version}</Pill>,
            },
            {
              key: "id",
              header: "ID",
              render: (s) => (
                <code style={{ fontSize: "0.78rem", color: "var(--text-dim)" }}>
                  {s.id}
                </code>
              ),
            },
          ]}
          rows={q.data}
          rowKey={(s) => s.id}
          empty={
            <EmptyState
              title="No secrets for this tenant"
              description="Create one — the plaintext is base64-encoded in your browser before submit, the server envelope-encrypts under the active KEK, and we never see it again."
            />
          }
        />
      )}

      <Modal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        closeDisabled={creating}
        title={`Create secret for ${tenantSlug}`}
        width={560}
      >
        <TenantSecretCreateForm
          accountSlug={accountSlug}
          tenantSlug={tenantSlug}
          onDone={() => setCreateOpen(false)}
          onPendingChange={setCreating}
        />
      </Modal>
    </>
  );
}

const primaryBtn: React.CSSProperties = {
  background: "var(--accent)",
  color: "var(--bg)",
  border: "none",
  padding: "8px 16px",
  borderRadius: 4,
  fontSize: "0.9rem",
  fontWeight: 600,
  cursor: "pointer",
};
